export type ReplacementVerdict = 'REPLACE' | 'REVIEW' | 'KEEP' | 'NOT_ASSESSED';

export interface ReplacementDimension {
  key: 'cost' | 'cycles' | 'condition';
  label: string;
  /** Null when the dimension could not be assessed at all. */
  pressure: number | null;
  unassessedBecause?: string;
  statement: string;
}

export interface ReplacementAssessment {
  equipmentId: string;
  assetNumber: string;
  name: string;
  verdict: ReplacementVerdict;
  score: number | null;
  drivenBy: ReplacementDimension['key'] | null;
  dimensions: ReplacementDimension[];
  confidence: 'high' | 'directional' | 'none';
}

import { api } from '../client';

// ============================================
// Phase 1B: Rotable Asset Management
// ============================================

export interface RotableAssetInfo {
  equipmentId: string;
  assetNumber: string;
  name: string;
  rotableStatus: string | null;
  totalCycles: number;
  currentLocation: {
    id: string;
    name: string;
  };
  lastMovement: {
    type: string;
    date: string;
    condition: string | null;
  } | null;
  utilizationRate: number;
  costPerUse: number;
  estimatedRemainingLife: number | null;
  repairHistory: Array<{
    date: string;
    cost: number;
    description: string;
  }>;
}

export interface UtilizationMetrics {
  equipmentId: string;
  assetNumber: string;
  name: string;
  /** Days committed to a job, open assignments counted up to today. */
  daysInUse: number;
  /** Days since acquisition, or null when we do not know when that was. */
  daysAvailable: number | null;
  /** Null when it cannot be computed. Zero would say it never worked. */
  utilizationRate: number | null;
  trips: number;
  outNow: boolean;
  utilizationBasis: string;
  totalCycles: number;
  acquisitionCost: number;
  acquisitionCostSource: 'ACQUISITION_COST' | 'PURCHASE_PRICE' | 'NONE';
  cumulativeRepairCost: number;
  totalCost: number;
  costPerCycle: number;
  costPerDay: number;
}

export interface RotableMovement {
  id: string;
  equipmentId: string;
  movementType: string;
  fromLocation?: {
    id: string;
    name: string;
    code: string;
  };
  toLocation?: {
    id: string;
    name: string;
    code: string;
  };
  workOrder?: {
    id: string;
    workOrderNumber: string;
    title: string;
  };
  condition: string | null;
  notes: string | null;
  createdAt: string;
}

export const rotableApi = {
  /**
   * Record asset movement
   */
  async recordMovement(data: {
    equipmentId: string;
    movementType: 'ISSUE' | 'RETURN' | 'TRANSFER' | 'INSPECT' | 'REPAIR' | 'SCRAP';
    fromLocationId?: string;
    toLocationId?: string;
    workOrderId?: string;
    condition?: 'EXCELLENT' | 'GOOD' | 'FAIR' | 'POOR' | 'FAILED';
    notes?: string;
  }) {
    const response = await api.post('/equipment/rotable/movement', data);
    return response.data;
  },

  /**
   * Get rotable asset information
   */
  async getAssetInfo(equipmentId: string) {
    const response = await api.get<RotableAssetInfo>(`/equipment/rotable/${equipmentId}`);
    return response.data;
  },

  /**
   * Get utilization metrics for all rotable assets
   */
  async getUtilizationMetrics() {
    const response = await api.get<UtilizationMetrics[]>('/equipment/rotable/utilization');
    return response.data;
  },

  /**
   * Get underutilized assets
   */
  async getUnderutilizedAssets(threshold: number = 30) {
    const response = await api.get<UtilizationMetrics[]>('/equipment/rotable/underutilized', {
      params: { threshold },
    });
    return response.data;
  },

  /**
   * Get assets recommended for replacement
   */

  /**
   * Get movement history for an asset
   */
  async getMovementHistory(equipmentId: string, limit: number = 50) {
    const response = await api.get<RotableMovement[]>(`/equipment/rotable/${equipmentId}/history`, {
      params: { limit },
    });
    return response.data;
  },
  /** Repair-versus-replace for every rotable, with the arithmetic behind it. */
  async getReplacementAssessment() {
    const response = await api.get<ReplacementAssessment[]>(
      '/equipment/rotable/replacement-assessment'
    );
    return response.data;
  },
};
